import * as React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { isSupportViewer } from '../../../utils/permissionUtils';

interface SettingFormProps {
    schema: any;
    onSubmit: (data: any) => void;
    formData?: any;
    formJson: any;
}

export const SettingForm = ({ schema, onSubmit, formData, formJson }: SettingFormProps) => {
    const [base64Files, setBase64Files] = React.useState<Record<string, string>>({});
    const [previews, setPreviews] = React.useState<Record<string, string>>({});
    const isSupport = isSupportViewer();

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
    });

    const onSubmitForm = (data: any) => {
        if (isSupport) return;
        const mergedData = {
            ...data,
            ...base64Files,
        };
        onSubmit(mergedData);
    };

    React.useEffect(() => {
        reset(formData);

        if (formJson) {
            formJson.forEach((row: any) => {
                row.cols.forEach((col: any) => {
                    if (col.type === 'file' && formData?.[col.name]) {
                        setPreviews((prev) => ({
                            ...prev,
                            [col.name]: formData[col.name],
                        }));
                    }
                });
            });
        }
    }, [formData, formJson, reset]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, name: string) => {
        if (isSupport) return;
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = reader.result as string;

                setBase64Files((prev) => ({
                    ...prev,
                    [name]: base64,
                }));

                setPreviews((prev) => ({
                    ...prev,
                    [name]: base64,
                }));

                setValue(name, file);
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmitForm)}>
            {formJson.map((item: any, index: number) => (
                <div key={`row_${index}`} className="row">
                    {item.cols.map((col: any, key: number) => {
                        const error = errors[col.name];

                        return (
                            <div
                                className={`col ${col?.md ? `col-md-${col.md}` : ''} ${col?.sm ? `col-sm-${col.sm}` : ''}`}
                                key={`Col.${key}`}
                            >
                                <div className="mb-3">
                                    <label htmlFor={col.name} style={{ fontSize: 14 }}>
                                        {col.label}
                                    </label>

                                    {col?.type === 'select' ? (
                                        <select
                                            id={col.name}
                                            {...register(col.name)}
                                            disabled={isSupport}
                                            className={`form-control ${error ? 'is-invalid' : ''}`}
                                        >
                                            <option value="">Select</option>
                                            {col?.list.map((item: any, idx: number) => (
                                                <option value={item?.value || ''} key={`${idx}-${item.label}`}>
                                                    {item.label}
                                                </option>
                                            ))}
                                        </select>
                                    ) : col?.type === 'textarea' ? (
                                        <textarea
                                            id={col.name}
                                            {...register(col.name)}
                                            disabled={isSupport}
                                            className={`form-control ${error ? 'is-invalid' : ''}`}
                                        />
                                    ) : col?.type === 'file' ? (
                                        <div className="d-flex align-items-center gap-3 mt-1">
                                            <input
                                                id={col.name}
                                                type="file"
                                                accept={col?.accept || '*'}
                                                disabled={isSupport}
                                                className={`form-control ${error ? 'is-invalid' : ''}`}
                                                style={{ maxWidth: '80%' }}
                                                onChange={(e) => handleFileChange(e, col.name)}
                                            />

                                            {previews[col.name] && (
                                                previews[col.name].startsWith('data:image') ? (
                                                    <div style={{ width: '20%',marginTop: '-8px' }}>
                                                        <img
                                                            src={previews[col.name]}
                                                            alt="Preview"
                                                            style={{
                                                                maxWidth: '38px',
                                                                objectFit: 'cover',
                                                                border: '1px solid #ccc',
                                                                borderRadius: '4px',
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <span style={{ fontSize: '0.85rem', color: '#333' }}>
                                                        File: {formData?.[col.name] || 'Uploaded file'}
                                                    </span>
                                                )
                                            )}
                                        </div>
                                    ) : (
                                        <input
                                            id={col.name}
                                            type={col?.type || 'text'}
                                            {...register(col.name)}
                                            disabled={isSupport}
                                            className={`form-control ${error ? 'is-invalid' : ''}`}
                                        />
                                    )}

                                    {error && (
                                        <div className="invalid-feedback" style={{ display: 'block' }}>
                                            {typeof error?.message === 'string' ? error.message : ''}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}

            {!isSupport && (
                <div className="mb-1 row">
                    <div className="col-md-10" />
                    <div className="col-md-2">
                        <button type="submit" className="btn btn-primary w-100">
                            Submit
                        </button>
                    </div>
                </div>
            )}
        </form>
    );
};

export default SettingForm;
